/* eslint-disable react/jsx-no-bind */
import {
  Component, Event,
  h, Listen, Prop, State, Watch, Element

} from '@stencil/core';
import {makeLogger} from '../../../logger/logger';
import type { labelProviderInterface } from '../../LabelProviderInterface';


interface SortOptions {
  id: string,
  displayName: string,
  selected: boolean,
}


const {
  info,
  warn
} = makeLogger({pathToCurrentFile: 'components/design-system/sorting-dropdown/svs-sorting-dropdown.tsx'});

@Component({
  tag: 'svs-sorting-dropdown',
  styleUrl: 'svs-sorting-dropdown.scss',
  shadow: true,
})

export class SvsSortingDropdown {

  @Prop({mutable: true}) sortOptions: Array<SortOptions> = [];
  private defaultValue: SortOptions;

  @Prop({mutable: true}) labelProvider?: labelProviderInterface;

  @Prop() readonly defaultValueId: string = '';

  @Prop() readonly disabled: boolean = false;

  @Prop() readonly scrollToTop: boolean = false;

  @State() opened = false;

  @State() selectedValue: SortOptions | undefined;

  @Listen('click', {target: 'window'})
  handleClick(event: MouseEvent) {
      if (this.opened && !this.el.contains(event.target as Node)) {
        this.opened = false;
      }
  }

  @Element() readonly el: Element;

  @Event({
    eventName: 'search:sortingSelected',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    sortingOptionSelected;


  @Event({
    eventName: 'uiaas:scrollToTop',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    handleScrollTop;

  private toggleOpen = (e): void => {
      if (!this.disabled) {
        e.stopPropagation();
        this.opened = !this.opened;
      }

      if (!this.opened) {
        this.qdsDropdown?.getRootNode()?.host?.click();
      }
  };

  @Watch('defaultValueId')
  updateSelectedValue() {
      this.selectedValue = this.sortOptions.find(el => el.id === this.defaultValueId);
  }

  componentWillLoad() {
      if (!this.labelProvider) {
        this.labelProvider = (name, fallback) => {
            info(
              'LabelProvider not provided in svs-sorting-dropdown component',
              [name, fallback]
            );
            return fallback;
        };
      }

      this.defaultValue = this.handleDefaultValue();

      if (!this.sortOptions[0]?.id || !this.sortOptions[0]?.displayName) {
        this.sortOptions = [];
        warn('No sorting correct options were provided to svs-sorting-dropdown component. Unable to render');
      }
  }

  private handleSelectElement = (e): void => {
      this.selectedValue = this.sortOptions.filter(el => el?.id === e.target.dataset?.id)[0];
      this.sortingOptionSelected.emit(this.selectedValue);
      if (this.scrollToTop) {
        this.handleScrollTop.emit();
      }

  };

  private handleButtonLabel = (sortOptions) => {
      sortOptions?.map(el => {
          if (el.selected) this.selectedValue = el;
      });
      const currentSortingMethod = this.selectedValue || this.defaultValue;
      const sortBy = this.labelProvider('sort-by', 'Sort by:');
      const sortMethod = this.labelProvider(`sort-by.${currentSortingMethod?.id}`, currentSortingMethod?.displayName);
      return `${sortBy} ${sortMethod}`;
  };

  private handleSelectedValue = (el) => {
      return el.selected || this.selectedValue?.id === el.id || (!this.selectedValue && this.defaultValue?.id === el.id);
  };

  private handleDefaultValue = () => {
      if (this.defaultValueId.length > 0) {
        const sortedById = this.sortOptions.find(el => el.id === this.defaultValueId);
        return sortedById || this.sortOptions[0];
      }
      return this.sortOptions[0];
  };

  private targetButton: HTMLElement;
  private qdsDropdown;

  private setRef = (el: HTMLElement) => {
      this.targetButton = el;
  };

  render() {
      return (
        this.sortOptions.length ? (
          <section onClick={this.toggleOpen} class="svs-sorting">
            <button class="svs-sorting-cta" ref={this.setRef}>
              <span>{this.handleButtonLabel(this.sortOptions)}</span>
              <se-icon
                size="micro"
                class={{'svs-sorting-cta__icon': true, 'svs-sorting-cta__icon--inversed': this.opened}}
              >
                arrow2_down
              </se-icon>
            </button>
            <qds-dropdown class="svs-sorting-dropdown" target={() => this.targetButton} ref={el => this.qdsDropdown = el} strategy="absolute">
              {this.sortOptions.map(el => (
                <qds-list-item
                  data-id={el.id}
                  onClick={this.handleSelectElement}
                  onKeyDown={e => e.key === 'Enter' && this.handleSelectElement(e)}
                  tabIndex={0}
                  text={this.labelProvider(`sort-by.${el.id}`, el.displayName)}
                  selected={this.handleSelectedValue(el)}
                />
              ))}
            </qds-dropdown>
          </section>
        ) : null
      );
  }
}
